import { NestAPI } from '@marketing-milk/frontend'
import { Comment, User } from '@marketing-milk/interfaces'
import { useQuery } from 'react-query'

interface UseGetCreativeMessagesProps {
  businessID: string
  adCreativeID: number
  enabled?: boolean
}

// Back-compat shape for CreativeWizard.tsx — surfaces fields the existing
// component reads (`user`, `message`, unix-int `createdAt`) on top of the
// unified Comment payload.
export interface CreativeMessage {
  id: number
  user: User
  message: string
  createdAt: number
  updatedAt: number
}

// Substituted when a comment's author has been deleted (FK SET NULL).
// Keeps the existing wizard UI rendering unchanged without scattering null checks.
const deletedUserPlaceholder: User = {
  id: 0,
  firstName: 'Deleted',
  lastName: 'User',
  email: '',
  profilePicture: '',
} as unknown as User

const toCreativeMessage = (c: Comment): CreativeMessage => ({
  id: c.id,
  user: c.author ?? deletedUserPlaceholder,
  message: c.body,
  createdAt: Math.floor(new Date(c.createdAt).getTime() / 1000),
  updatedAt: Math.floor(new Date(c.updatedAt).getTime() / 1000),
})

const fetchCreativeMessages = async (
  businessID: string,
  adCreativeID: number
): Promise<CreativeMessage[]> => {
  const response = await NestAPI.get<Comment[]>(
    `businesses/${businessID}/comments?anchorType=creative&anchorID=${adCreativeID}`
  )
  return response.data.map(toCreativeMessage)
}

export const useGetCreativeMessages = ({
  businessID,
  adCreativeID,
  enabled = true,
}: UseGetCreativeMessagesProps) => {
  const {
    data: comments = [],
    isLoading,
    error,
    refetch,
  } = useQuery<CreativeMessage[], Error>(
    ['creativeMessages', businessID, adCreativeID],
    () => fetchCreativeMessages(businessID, adCreativeID),
    {
      enabled,
      refetchInterval: 60000,
      refetchOnWindowFocus: true,
      staleTime: 30000,
    }
  )

  return { comments, isLoading, error, refetch }
}
